/**
 * @progression/host-bundle (L6 substrate, Agent 7).
 *
 * The deployment-neutral mount that every host target shares. It pins down "the
 * same engine runs everywhere" in one place so the four hosts differ ONLY in how
 * they persist the event-log snapshot:
 *
 *   - the REAL L3 `Store` (Agent 4) over the real L2 reducer + L1 kernel — verdicts
 *     are re-derived purely from the current config every query (INV-CORE,
 *     INV-NOEQUIV); nothing derived is ever persisted;
 *   - `mountSlice(adapter)` — load (or seed) the §9.1 snapshot through a
 *     `HostAdapter`, replay it into a fresh store (re-derive, never trust a cache),
 *     and paint it through the renderer + provider;
 *   - `computeSliceVerdicts(store)` — the four §9.1 behaviours as data, so a host
 *     smoke test can assert byte-identical verdicts on every target;
 *   - `seedSnapshot` / `snapshotOf` / `restore` — the persistence-discipline helpers.
 *
 * INV-DEP: this package imports DOWNWARD only — `@progression/app`,
 * `@progression/view` (L4), `@progression/i18n` (L5), and `@progression/contracts`
 * (never `core`). This is exactly the edge the architecture permits for the host
 * substrate (Architecture_V2.01.md §4: `host-bundle → app, view, i18n, contracts`),
 * so the deployment bundle ships the REAL L4 view-model and the REAL L5 i18n
 * provider + en/ar/hi catalogs on every target.
 *
 * `mountSlice` now builds the production `buildViewModel` (Agent 5, L4) and
 * localizes through the production `createI18nProvider` over the en/ar/hi catalogs
 * (Agent 6, L5). The renderer is injectable (§6.4): the default is a headless
 * text renderer so the Node mount path + host smoke tests run without a DOM, and
 * a DOM target passes `createDomRenderer` (re-exported here) to paint for real.
 * The previous Phase-1 `createStubProvider` / `createStubRenderer` /
 * `buildStubViewModel` helpers remain exported for back-compatible headless use.
 * (Closes CHECKER_LOG Run-1 finding #1; supersedes DECISIONS_Agent7.md D0/D1/D1a.)
 */
import { createStore, createMockStore, CONTRACT_VERSION } from '@progression/app';
import { buildViewModel } from '@progression/view';
import { createI18nProvider, EN_PHASE2_CATALOG, AR_CATALOG, HI_CATALOG, } from '@progression/i18n';
import { SEED_LOG, C, CL, EXT_OBSERVATION_EDGE, RESOURCE_EDGE, } from '@progression/contracts/fixtures';
import { messageId } from '@progression/contracts';
// Re-export the real L3 store factories + the bound contract version so a host
// imports its whole engine surface from one place.
export { createStore, createMockStore, CONTRACT_VERSION };
export { SEED_LOG, C, CL } from '@progression/contracts/fixtures';
// Re-export the REAL L4 + L5 surfaces so a host imports its whole engine surface
// (kernel → store → view-model → renderer → i18n) from this one place.
export { buildViewModel, createDomRenderer } from '@progression/view';
export { createI18nProvider, EN_PHASE2_CATALOG, AR_CATALOG, HI_CATALOG } from '@progression/i18n';
// Compute Integration (L6 edge): the external-compute producer + the single async
// orchestration. Imports app/view/contracts only — never core (INV-DEP / F7).
export { createMockNumpyResource, createPyodideNumpyResource, runComputation, assertSandboxed, ComputeUnavailableError, } from './compute.js';
/**
 * The production L5 provider over the shipped en/ar/hi catalogs (Agent 6). Every
 * host localizes through this; adding a language stays a catalog drop-in.
 */
export function createProvider() {
    return createI18nProvider([EN_PHASE2_CATALOG, AR_CATALOG, HI_CATALOG]);
}
// ───────────────────────────────────────────────────────────────────────────
// §6.5 I18nProvider — MINIMAL STUB (English only). Replace with @progression/i18n.
// ───────────────────────────────────────────────────────────────────────────
const EN_MESSAGES = Object.freeze({
    'app.title': 'Progression slice',
    'claim.scalar-gravity': 'Scalar gravity',
    'claim.non-euclidean': 'Non-Euclidean geometry',
    'claim.substrate-sr': 'Substrate special relativity',
    'state.explorable': 'explorable',
    'state.refuted': 'refuted',
    'state.outside': 'outside',
});
/** A tiny English-only §6.5 provider built on the frozen contract types only. */
export function createStubProvider() {
    return {
        t(id, params, _locale) {
            const base = EN_MESSAGES[id] ?? String(id);
            if (params === undefined)
                return base;
            return Object.keys(params).reduce((acc, k) => acc.replaceAll(`{${k}}`, String(params[k])), base);
        },
        dir(_locale) {
            return 'ltr';
        },
        format(value, locale, opts) {
            return value instanceof Date
                ? new Intl.DateTimeFormat(locale, opts).format(value)
                : new Intl.NumberFormat(locale, opts).format(value);
        },
        fontStack(_script) {
            return 'system-ui, -apple-system, sans-serif';
        },
        scriptOf(_locale) {
            return 'latin'; // English-only stub: Latin always (replace with @progression/i18n).
        },
        hasCatalog(locale) {
            return locale === 'en' || locale.startsWith('en-');
        },
    };
}
/**
 * A headless §6.4 `Renderer` that flattens a `ViewModel` to text via the provider
 * at the given `locale` (default `en`). Used as the DOM-free default paint target
 * for the Node mount path + host smoke tests; a DOM target uses `createDomRenderer`.
 */
export function createStubRenderer(locale = 'en') {
    const painted = [];
    let mounted = null;
    const paint = (vm, t) => {
        const walk = (node) => {
            const self = t.t(node.text.key, node.text.params, locale);
            const kids = (node.children ?? []).map(walk).join(', ');
            return kids.length > 0 ? `${self}: [${kids}]` : self;
        };
        painted.push(walk(vm.root));
    };
    return {
        mount(host) {
            mounted = typeof host === 'string' ? host : 'element';
        },
        render(vm, t) {
            paint(vm, t);
        },
        update(_prev, next, t) {
            paint(next, t);
        },
        get painted() {
            return painted;
        },
        lastText() {
            return painted[painted.length - 1] ?? '';
        },
        mountedOn() {
            return mounted;
        },
    };
}
// ───────────────────────────────────────────────────────────────────────────
// A minimal §6.4 ViewModel built from the store, so the mount paints real verdicts.
// ───────────────────────────────────────────────────────────────────────────
function stateToken(state) {
    return state === 'refuted' ? 'state-refuted' : 'state-explorable';
}
function str(key, params) {
    return params === undefined ? { key: messageId(key) } : { key: messageId(key), params };
}
/**
 * A small, locale-neutral §6.4 view-model listing the §9.1 claims with their
 * re-derived verdict state. (Stub — Agent 5's `buildViewModel` is the production
 * builder; this exists so the bundle can be *mounted and rendered* end-to-end.)
 */
export function buildStubViewModel(store, register = 'full') {
    const state = store.getState();
    const ids = [CL.scalarGravity, CL.nonEuclidean, CL.substrateSr];
    const children = ids.map((id) => {
        const verdict = store.verdictOf(id);
        const labelId = state.claims.get(id)?.labelId ?? messageId(String(id));
        const child = {
            token: stateToken(verdict.state),
            text: { key: labelId },
            children: [{ token: 'neutral', text: str(`state.${verdict.state}`) }],
        };
        return child;
    });
    return {
        register,
        dirHint: 'auto',
        root: { token: 'neutral', text: str('app.title'), children },
        precisionLoss: Object.freeze([]),
    };
}
// ───────────────────────────────────────────────────────────────────────────
// Persistence discipline (Deliverable B): store inputs, re-derive verdicts.
// ───────────────────────────────────────────────────────────────────────────
/** The §9.1 seed snapshot: the authoritative input + the contract it binds to. */
export function seedSnapshot() {
    return { events: SEED_LOG, contractVersion: CONTRACT_VERSION };
}
/** A snapshot of a store's authoritative log (never a derived verdict cache). */
export function snapshotOf(store) {
    return { events: store.exportLog(), contractVersion: store.contractVersion() };
}
/** Rebuild a store by replaying a snapshot's event log (INV-DET, INV-NOEQUIV). */
export function restore(snapshot) {
    const store = createStore();
    store.replay(snapshot.events);
    return store;
}
/** An in-memory `StorageLike` for headless tests (and a no-DOM fallback). */
export class MemoryStorage {
    map = new Map();
    getItem(key) {
        return this.map.has(key) ? this.map.get(key) : null;
    }
    setItem(key, value) {
        this.map.set(key, value);
    }
    removeItem(key) {
        this.map.delete(key);
    }
}
const DEFAULT_STORAGE_KEY = 'progression.snapshot.v1';
/** Serialize a snapshot for JSON export/import (round-trips exactly). */
export function exportSnapshotJson(snapshot) {
    return JSON.stringify(snapshot);
}
/** Parse a snapshot from JSON, validating the minimal shape. */
export function importSnapshotJson(text) {
    const parsed = JSON.parse(text);
    if (typeof parsed !== 'object' ||
        parsed === null ||
        !Array.isArray(parsed.events) ||
        typeof parsed.contractVersion !== 'string') {
        throw new Error('importSnapshotJson: not a valid Snapshot');
    }
    return parsed;
}
/**
 * A `HostAdapter` backed by a `StorageLike` (localStorage in the browser, an
 * injected `MemoryStorage` in tests). Persists ONLY the event-log snapshot;
 * verdicts are re-derived on load by the mount (INV-NOEQUIV).
 */
export function createStorageAdapter(storage, opts = {}) {
    const key = opts.key ?? DEFAULT_STORAGE_KEY;
    const tier = opts.tier ?? 'BEST-EFFORT';
    return {
        load() {
            const raw = storage.getItem(key);
            return Promise.resolve(raw === null ? null : importSnapshotJson(raw));
        },
        save(snap) {
            storage.setItem(key, exportSnapshotJson(snap));
            return Promise.resolve();
        },
        isolationTier() {
            return tier;
        },
    };
}
/**
 * Export the persisted event-log snapshot as JSON (ownership / no-lock-in,
 * Deliverable D). Works on every target through its `HostAdapter`; if nothing is
 * stored yet, exports the §9.1 seed.
 */
export async function exportFrom(adapter) {
    const snap = (await adapter.load()) ?? seedSnapshot();
    return exportSnapshotJson(snap);
}
/**
 * Import an event-log snapshot from JSON and persist it through the adapter
 * (download → upload round-trip). Returns the imported snapshot.
 */
export async function importSnapshotInto(adapter, json) {
    const snap = importSnapshotJson(json);
    await adapter.save(snap);
    return snap;
}
/**
 * Mount the real L1–L5 bundle through a host's persistence adapter. On first run
 * the adapter has nothing stored, so we seed the §9.1 snapshot and save it; on
 * every run we replay the persisted log into a fresh store (re-derive verdicts,
 * never trust a cache), build the REAL `buildViewModel` (L4), and paint it once
 * through the renderer, localized by the REAL en/ar/hi i18n provider (L5).
 */
export async function mountSlice(adapter, options = {}) {
    const loaded = await adapter.load();
    const snapshot = loaded ?? seedSnapshot();
    if (loaded === null)
        await adapter.save(snapshot);
    const contractMatches = snapshot.contractVersion === CONTRACT_VERSION;
    const store = createStore();
    store.replay(snapshot.events);
    const locale = options.locale ?? 'en';
    const register = options.register ?? 'full';
    const provider = options.provider ?? createProvider();
    const renderer = options.renderer ?? createStubRenderer(locale);
    const mountPoint = options.mountPoint ?? '#progression-root';
    // Real L4 view-model from the replayed state, painted through the real L5 provider.
    const viewModel = buildViewModel(store.getState(), register);
    renderer.mount(mountPoint);
    renderer.render(viewModel, provider);
    return {
        store,
        renderer,
        provider,
        locale,
        register,
        viewModel,
        snapshot,
        contractMatches,
        isolationTier: adapter.isolationTier(),
    };
}
/** The proved ground truth (EngineV2.Demo → false/true/true/false), as states. */
export const EXPECTED_SLICE = Object.freeze({
    scalarGravityDefault: 'refuted',
    substrateSrDefault: 'explorable',
    scalarGravityWhenChallenged: 'explorable',
    scalarGravityAfterInertEdge: 'refuted',
    substrateSrAfterInertEdge: 'explorable',
    substrateSrAfterConstraintImport: 'refuted',
});
/**
 * Run the four §9.1 behaviours against a mounted store. Base verdicts come from
 * the host's actual mounted store; the mutating behaviours (#2, #4) and the
 * inert-edge check (#3) run on clones replayed from the same authoritative log,
 * so the result is a pure function of the seed and identical on every host.
 */
export function computeSliceVerdicts(store) {
    const base = store.exportLog();
    const challenged = createStore(base);
    challenged.dispatch({ c: 'challenge', id: C.lightBends });
    const inert = createStore(base);
    inert.dispatch({ c: 'import-edge', edge: RESOURCE_EDGE });
    const imported = createStore(base);
    imported.dispatch({ c: 'import-edge', edge: EXT_OBSERVATION_EDGE });
    return {
        scalarGravityDefault: store.verdictOf(CL.scalarGravity).state,
        substrateSrDefault: store.verdictOf(CL.substrateSr).state,
        scalarGravityWhenChallenged: challenged.verdictOf(CL.scalarGravity).state,
        scalarGravityAfterInertEdge: inert.verdictOf(CL.scalarGravity).state,
        substrateSrAfterInertEdge: inert.verdictOf(CL.substrateSr).state,
        substrateSrAfterConstraintImport: imported.verdictOf(CL.substrateSr).state,
    };
}
//# sourceMappingURL=index.js.map